<template>
  <b-form autocomplete="off" method="POST" @submit.prevent="submit">

    <b-form-group :label="$t('applications.labels.fullName')" label-for="formFullName">
      <b-form-input id="formFullName" type="text" required placeholder="e.g. John Smith" v-model="form.fullName"
        :class="{ 'is-invalid': typeof isValid.fullName === 'string' }" />
      <div class="invalid-feedback" v-if="isValid.fullName === 'EMPTY'">
        {{ $t('applications.validation.fullName-empty') }}
      </div>
      <div class="invalid-feedback" v-if="isValid.fullName === 'TOO_SHORT'">
        {{ $t('applications.validation.fullName-too-short') }}
      </div>
    </b-form-group>

    <b-form-group :label="$t('applications.labels.emailAddress')" label-for="emailAddress">
      <b-form-input id="emailAddress" type="email" required placeholder="e.g. john.smith@gmail.com" v-model="form.emailAddress"
        :class="{ 'is-invalid': typeof isValid.emailAddress === 'string' }" />
      <div class="invalid-feedback" v-if="isValid.emailAddress === 'MISSING_AT'">
        {{ $t('applications.validation.emailAddress-invalid') }}
      </div>
      <div class="invalid-feedback" v-if="isValid.emailAddress === 'TOO_SHORT'">
        {{ $t('applications.validation.emailAddress-too-short') }}
      </div>
    </b-form-group>

    <b-form-group :label="$t('applications.labels.phoneNumber')" label-for="formLastName">
      <b-form-input id="formPhone" type="text" required placeholder="e.g. +447500123456" v-model="form.phoneNumber"
        :class="{ 'is-invalid': typeof isValid.phoneNumber === 'string' }"/>
      <div class="invalid-feedback" v-if="isValid.phoneNumber === 'EMPTY'">
        {{ $t('applications.validation.phoneNumber-empty') }}
      </div>
      <div class="invalid-feedback" v-if="isValid.phoneNumber === 'TOO_SHORT'">
        {{ $t('applications.validation.phoneNumber-too-short') }}
      </div>
    </b-form-group>

    <b-form-group :label="$t('applications.labels.resume')" label-for="formResume" v-if="typeof applyWithResume === 'boolean'">
      <asset-block class="m-0" accept=".txt,.rtf,.pdf,.doc,.docx,.odt" :field="{ type: 'DOCUMENT' }"
        id="formResume" :placeholder="$t('applications.placeholders.resume')" v-model="form.resume"/>
      <div class="d-block invalid-feedback" v-if="isValid.resume === 'MISSING_FILE'">
        {{ $t('applications.validation.resume-empty') }}
      </div>
    </b-form-group>

    <b-form-group :label="$t('applications.labels.coverLetter')" label-for="formCoverLetter" v-if="typeof applyWithCoverLetter === 'boolean'">
      <asset-block class="m-0" accept=".txt,.rtf,.pdf,.doc,.docx,.odt" :field="{ type: 'DOCUMENT' }"
        id="formCoverLetter" placeholder="Select a cover letter" v-model="form.coverLetter"/>
      <div class="d-block invalid-feedback" v-if="isValid.coverLetter === 'MISSING_FILE'">
        {{ $t('applications.validation.coverLetter-empty') }}
      </div>
    </b-form-group>

    <template v-if="onBehalf">
      <b-form-group :label="$t('jobs.apply.userAnswers-prompt')" label-for="userAnswersType">
        <b-form-text class="mb-2">{{ $t('jobs.apply.userAnswers-description') }}</b-form-text>
        <b-form-radio-group id="userAnswersType" v-model="form.userAnswersType">
          <b-form-radio value="RECOMMEND">{{ $t('applications.userAnswerTypes.RECOMMEND') }}</b-form-radio>
          <b-form-radio value="INTRODUCE">{{ $t('applications.userAnswerTypes.INTRODUCE') }}</b-form-radio>
        </b-form-radio-group>
        <b-form-textarea class="mt-2" id="comments" placeholder="Comments" v-model="form.userAnswersMessage"/>
        <b-form-text>{{ $t('jobs.apply.recommend-description') }}</b-form-text>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox id="termsAgreed" required v-model="form.termsAgreed" :class="{ 'is-invalid': isValid.termsAgreed }">
          <i18n path="applications.labels.termsAgreed-employee">
            <template v-slot:termsConditionsLink>
              <a href="/static/documents/Real+Links+Terms+%26+Conditions.pdf" tabindex="-1" target="_blank">{{ $t('general.terms-conditions') }}</a>
            </template>
            <template v-slot:privacyPolicyLink>
              <a href="https://www.reallinks.io/privacy-policy/" tabindex="-1" target="_blank">{{ $t('general.privacy-policy') }}</a>
            </template>
          </i18n>
        </b-form-checkbox>
        <div class="invalid-feedback" style="display: inherit" v-if="form.termsAgreed === false" >
          {{ $t('applications.validation.termsAgreed-employee-unchecked') }}
        </div>
      </b-form-group>
    </template>
    <b-form-group v-else>
      <b-form-checkbox id="termsAgreed" required v-model="form.termsAgreed" :class="{ 'is-invalid': isValid.termsAgreed }">
        <i18n path="applications.labels.termsAgreed-visitor">
          <template v-slot:termsConditionsLink>
            <a href="/static/documents/Real+Links+Terms+%26+Conditions.pdf" tabindex="-1" target="_blank">{{ $t('general.terms-conditions') }}</a>
          </template>
          <template v-slot:privacyPolicyLink>
            <a href="https://www.reallinks.io/privacy-policy/" tabindex="-1" target="_blank">{{ $t('general.privacy-policy') }}</a>
          </template>
        </i18n>
      </b-form-checkbox>
      <div class="invalid-feedback" style="display: inherit" v-if="form.termsAgreed === false" >
        {{ $t('applications.validation.termsAgreed-unchecked') }}
      </div>
    </b-form-group>

    <b-button type="submit" variant="primary" @click.prevent="submit">{{ $t('ctas.apply-now') }}</b-button>
  </b-form>
</template>

<script>
import AssetBlock from '@/components/settings/AssetBlock';
import assetUploadMixin from '@/mixins/assetUpload';
import { createSha1File } from '@/lib/hashing';
import { mapRoot, mapPreferences } from '@/mixins/root';

export default {
  props: {
    userID: String,
    jobID: String,
    referralID: String,
    onBehalf: {
      type: Boolean,
      default: () => false,
    },
  },
  components: {
    AssetBlock,
  },
  mixins: [ assetUploadMixin ],
  data: () => ({
    form: {},
  }),
  computed: {
    ...mapRoot([ 'isPayPal' ]),
    ...mapPreferences([ 'applyWithResume', 'applyWithCoverLetter' ]),
    isValid() {
      const isValid = {};

      if (this.form.hasOwnProperty('fullName')) {
        const { fullName } = this.form;

        if (fullName.length < 2) {
          isValid.fullName = 'TOO_SHORT';
        } else if (!fullName) {
          isValid.fullName = 'EMPTY';
        } else {
          isValid.fullName = true;
        }
      } else {
        isValid.fullName = null;
      }

      if (this.form.hasOwnProperty('phoneNumber')) {
        const { phoneNumber } = this.form;

        if (phoneNumber.length < 3) {
          isValid.phoneNumber = 'TOO_SHORT';
        } else if (!phoneNumber) {
          isValid.phoneNumber = 'EMPTY';
        } else {
          isValid.phoneNumber = true;
        }
      } else {
        isValid.phoneNumber = null;
      }

      if (this.form.hasOwnProperty('emailAddress')) {
        const { emailAddress } = this.form;

        if (emailAddress.length < 3) {
          isValid.emailAddress = 'TOO_SHORT';
        } else if (!emailAddress.includes('@')) {
          isValid.emailAddress = 'MISSING_AT';
        } else {
          isValid.emailAddress = true;
        }
      } else {
        isValid.emailAddress = null;
      }

      if (this.form.hasOwnProperty('resume')) {
        const { resume } = this.form;
        if (resume && resume.id) {
          isValid.resume = true;
        } else if (this.applyWithResume === true) {
          isValid.resume = 'MISSING_FILE';
        }
      }

      if (this.form.hasOwnProperty('coverLetter')) {
        const { coverLetter } = this.form;
        if (coverLetter && coverLetter.id) {
          isValid.coverLetter = true;
        } else if (this.applyWithCoverLetter === true) {
          isValid.coverLetter = 'MISSING_FILE';
        }
      }

      if (this.form.hasOwnProperty('termsAgreed')) {
        isValid.termsAgreed = this.form.termsAgreed === true;
      } else {
        isValid.termsAgreed = null;
      }

      return isValid;
    },
  },
  methods: {
    async uploadFile(field, event) {
      try {
        this.$assert(event.target.files && event.target.files.length, new Error('Expected a file to be selected'));

        const [ file ] = event.target.files;
        this.$assert(file && file.name, new Error('No file was selected'), {
          code: 'FILE_NOT_FOUND',
        });

        const { token, asset: uploading, upload } = await this.createAssetMutation({
          type: 'DOCUMENT',
          fileName: file.name,
          fileMime: file.type,
          fileSize: file.size,
          fileSha1: await createSha1File(file),
        });

        this.$log.debug({ file: file.name, uploading, upload });

        await this.initAssetUpload(upload, file);

        const asset = await this.uploadedAssetMutation(token);
        this.$assert(asset && asset.id, new Error('Failed to upload file'));
        this.form[field] = { id: asset.id };
      } catch (err) {
        this.$log.error(err);
        this.$notifyErr(err);
      }
    },
    submit() {
      this.form = Object.assign({
        fullName: '',
        emailAddress: '',
        phoneNumber: '',
        resume: null,
        coverLetter: null,
        termsAgreed: false,
      }, this.form);

      const isValid = Object.keys(this.isValid).reduce((result, key) => result && this.isValid[key] === true, true);
      this.$log.debug('isValid', isValid);

      if (isValid) {
        const {
          resume, coverLetter,
          userAnswersType, userAnswersMessage,
          ...create
        } = JSON.parse(JSON.stringify(this.form));

        if (resume && resume.id) {
          create.resume = { id: resume.id };
        }
        if (coverLetter && coverLetter.id) {
          create.coverLetter = { id: coverLetter.id };
        }
        if (userAnswersType && userAnswersMessage) {
          create.userAnswers = { type: userAnswersType, message: userAnswersMessage };
        }

        this.$emit('submit', create);
      }
    }
  },
};
</script>

<style lang="scss">
.apply-box {
  .btn {
    margin: 0 auto;
  }
  .btn-primary {
    margin-top: 1em;
    width: 100%;
  }

  .dropzone {
    padding: 0;
    width: 100%;

    .dz-message {
      margin: 1em 0;
    }
  }

}
</style>



// WEBPACK FOOTER //
// src/components/jobs/apply.vue